<script lang="ts">
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import Carousel from '$lib/carousel.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import TimedGalleryIndicator from '$lib/timedGalleryIndicator.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import { fly } from 'svelte/transition';
	import SimpleGalleryIndicator from '$lib/simpleGalleryIndicator.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';

	interface Props {
		content: ISbStoryData<{
			title: string;
			gallery_blocks: ISbStoryData[];
			variant: 'static' | 'autoplay';
			autoplay_duration: number;
		}>['content'];
	}

	let { content }: Props = $props();

	let embla = $state<EmblaCarouselType>();
	let currentIndex = $state(0);
</script>

<section
	use:editable={content}
	class="relative mb-4 mt-8 overflow-hidden pb-4 pt-8 lg:mb-8 lg:mt-12"
>
	<PageGrid16>
		<h1
			class="col-span-full mb-6 text-center text-[36px]/[112%] tracking-[-.36px] text-brown-1 max-lg:mx-5 lg:col-start-2 lg:col-end-[16] lg:mb-14 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
		>
			{content.title}
		</h1>

		<Carousel
			autoplay={!content.variant || content.variant === 'autoplay'}
			autoplayDuration={content.autoplay_duration}
			bind:embla
			bind:currentIndex
			overflow
			containerClass="col-span-full flex lg:col-start-3 lg:col-end-[15]"
			class="flex h-full w-full gap-x-4 max-lg:mx-10 lg:gap-x-16"
		>
			{#each content.gallery_blocks as item, index}
				<div class="flex-none basis-full">
					<Blok content={item} {index} />
				</div>
			{/each}
		</Carousel>

		{#if embla}
			<div class="col-span-full mt-6 lg:mt-8">
				{#if !content.variant || content.variant === 'autoplay'}
					<TimedGalleryIndicator
						{embla}
						theme="brown"
						count={content.gallery_blocks.length}
						active={currentIndex}
					/>
				{:else}
					<SimpleGalleryIndicator
						active={currentIndex}
						count={content.gallery_blocks.length}
						onchange={(idx) => embla?.scrollTo(idx)}
					/>
				{/if}
			</div>
		{/if}
	</PageGrid16>
	<div class="pointer-events-none absolute inset-0 mt-14 max-lg:hidden">
		{#if currentIndex > 0}
			<div
				transition:fly={{ x: '-100%', opacity: 1 }}
				class="absolute inset-y-0 left-5 flex items-center"
			>
				<Button class="btn-large btn-white pointer-events-auto" onclick={() => embla?.scrollPrev()}
					><ArrowLeft /></Button
				>
			</div>
		{/if}
		{#if currentIndex < content.gallery_blocks.length - 1}
			<div
				transition:fly={{ x: '100%', opacity: 1 }}
				class="absolute inset-y-0 right-5 flex items-center"
			>
				<Button class="btn-large btn-white pointer-events-auto" onclick={() => embla?.scrollNext()}
					><ArrowRight /></Button
				>
			</div>
		{/if}
	</div>
</section>
